import { ChevronDown, ChevronUp, Lock, Plus, Trash2 } from "lucide-react";
import {
  questionTypeLabel,
  type QuestionType,
  type UpsertReportFormQuestionPayload,
  type UpsertReportFormSectionPayload,
  type UpsertReportTemplatePayload,
} from "@/api/reportTemplates";
import { Badge, Button, FormField, Input } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { ValidationRulesEditor } from "./ValidationRulesEditor";

const QUESTION_TYPES: QuestionType[] = [
  "Text",
  "TextArea",
  "Number",
  "Date",
  "Select",
  "MultiSelect",
  "YesNo",
  "RatingScale",
  "FileAttachment",
  "LoanPicker",
  "PortfolioPicker",
  "BrokerPicker",
  "VendorPicker",
  "DealPicker",
];

interface ReportTemplateFormEditorProps {
  form: UpsertReportTemplatePayload;
  setForm: React.Dispatch<React.SetStateAction<UpsertReportTemplatePayload>>;
}

/**
 * LLM &amp; Reports v3.1 §10d — embedded form editor. Owns sections +
 * questions of the report template aggregate. Replaces the v2.0
 * /admin/form-templates form builder and surfaces the new picker
 * question types (LoanPicker / PortfolioPicker / BrokerPicker /
 * VendorPicker / DealPicker) in the type dropdown.
 *
 * Reorder is via Move-up / Move-down buttons rather than HTML5
 * drag-and-drop — same UX the v2.0 form builder used; no new
 * dependencies.
 */
export function ReportTemplateFormEditor({
  form,
  setForm,
}: ReportTemplateFormEditorProps) {
  const updateSection = (
    idx: number,
    patch: Partial<UpsertReportFormSectionPayload>,
  ) => {
    setForm((f) => ({
      ...f,
      sections: f.sections.map((s, i) => (i === idx ? { ...s, ...patch } : s)),
    }));
  };
  const updateQuestion = (
    sIdx: number,
    qIdx: number,
    patch: Partial<UpsertReportFormQuestionPayload>,
  ) => {
    setForm((f) => ({
      ...f,
      sections: f.sections.map((s, i) =>
        i === sIdx
          ? {
              ...s,
              questions: s.questions.map((q, j) =>
                j === qIdx ? { ...q, ...patch } : q,
              ),
            }
          : s,
      ),
    }));
  };

  const addSection = () =>
    setForm((f) => ({
      ...f,
      sections: [...f.sections, newSection(f.sections.length)],
    }));
  const removeSection = (sIdx: number) =>
    setForm((f) => ({
      ...f,
      sections: f.sections
        .filter((_, i) => i !== sIdx)
        .map((s, i) => ({ ...s, displayOrder: i })),
    }));
  const moveSection = (sIdx: number, dir: -1 | 1) =>
    setForm((f) => {
      const next = [...f.sections];
      const j = sIdx + dir;
      if (j < 0 || j >= next.length) return f;
      [next[sIdx], next[j]] = [next[j], next[sIdx]];
      return {
        ...f,
        sections: next.map((s, i) => ({ ...s, displayOrder: i })),
      };
    });

  const addQuestion = (sIdx: number) =>
    setForm((f) => ({
      ...f,
      sections: f.sections.map((s, i) =>
        i === sIdx
          ? { ...s, questions: [...s.questions, newQuestion(s.questions.length)] }
          : s,
      ),
    }));
  const removeQuestion = (sIdx: number, qIdx: number) =>
    setForm((f) => ({
      ...f,
      sections: f.sections.map((s, i) =>
        i === sIdx
          ? {
              ...s,
              questions: s.questions
                .filter((_, j) => j !== qIdx)
                .map((q, j) => ({ ...q, displayOrder: j })),
            }
          : s,
      ),
    }));
  const moveQuestion = (sIdx: number, qIdx: number, dir: -1 | 1) =>
    setForm((f) => ({
      ...f,
      sections: f.sections.map((s, i) => {
        if (i !== sIdx) return s;
        const next = [...s.questions];
        const j = qIdx + dir;
        if (j < 0 || j >= next.length) return s;
        [next[qIdx], next[j]] = [next[j], next[qIdx]];
        return {
          ...s,
          questions: next.map((q, k) => ({ ...q, displayOrder: k })),
        };
      }),
    }));

  const totalQuestions = form.sections.reduce(
    (acc, s) => acc + s.questions.length,
    0,
  );

  return (
    <section className="bg-white-pure border border-border-light rounded-lg p-space-4">
      <div className="flex items-center justify-between mb-space-3">
        <h2 className="font-display font-bold text-h3 text-onyx tracking-normal">
          <span className="text-gold-shadow mr-space-2 font-mono">2.</span>
          Form
        </h2>
        <span className="text-caption text-gray-mid">
          {form.sections.length} sections · {totalQuestions} questions
        </span>
      </div>

      <p className="text-caption text-gray-mid mb-space-3">
        Add the sections and questions users will fill out. Picker
        question types let users choose a Loan, Portfolio, Broker,
        Vendor, or Deal — the picked record's name and other details
        are automatically used in the report.
      </p>

      {/* v3.5 §7: ghost preview card showing the locked Report context
          fields exactly as they appear at the top of every form. Pure
          preview — no data binding, all inputs disabled. The lock icon
          + dashed border + faded styling reads as "managed by the
          system" so the template author understands they configure
          their own questions below, not these. */}
      <div
        role="note"
        aria-label="Locked system fields preview"
        className="mb-space-3 rounded-md border border-dashed border-gold-shadow/50 bg-white-pure p-space-3 opacity-80"
      >
        <div className="flex items-center justify-between mb-space-2">
          <span className="inline-flex items-center gap-space-1 font-display text-caption uppercase tracking-label text-gold-shadow">
            <Lock size={12} aria-hidden /> Report context
          </span>
          <span className="text-caption italic text-gray-mid">
            Locked — system fields, appear on every template
          </span>
        </div>
        <div className="space-y-space-3">
          <GhostPreviewField
            label="Report subject"
            optional
            helper="What is this report about? Briefly describe the company, situation, or topic."
          />
          <GhostPreviewField
            label="Prepared for / Audience"
            optional
            helper={"Who is the intended audience? Examples: “Investment Committee”, “Underwriting Team”."}
          />
        </div>
      </div>

      <div className="space-y-space-3">
        {form.sections.map((section, sIdx) => (
          <div
            key={section.id ?? `s-${sIdx}`}
            className="border border-border-light rounded-lg bg-white-warm p-space-3"
          >
            <div className="flex items-center justify-between mb-space-2">
              <h3 className="font-display font-bold text-small uppercase tracking-wider">
                Section {sIdx + 1}
              </h3>
              <div className="flex items-center gap-space-1">
                <Button
                  type="button"
                  variant="tertiary"
                  size="sm"
                  onClick={() => moveSection(sIdx, -1)}
                  disabled={sIdx === 0}
                  aria-label="Move section up"
                >
                  <ChevronUp size={14} aria-hidden />
                </Button>
                <Button
                  type="button"
                  variant="tertiary"
                  size="sm"
                  onClick={() => moveSection(sIdx, 1)}
                  disabled={sIdx === form.sections.length - 1}
                  aria-label="Move section down"
                >
                  <ChevronDown size={14} aria-hidden />
                </Button>
                <Button
                  type="button"
                  variant="tertiary"
                  size="sm"
                  onClick={() => removeSection(sIdx)}
                  aria-label="Remove section"
                >
                  <Trash2 size={14} aria-hidden /> Remove
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-space-2">
              <FormField label="Title" required>
                <Input
                  value={section.title}
                  onChange={(e) => updateSection(sIdx, { title: e.target.value })}
                />
              </FormField>
              <FormField label="Description (optional)">
                <Input
                  value={section.description ?? ""}
                  onChange={(e) =>
                    updateSection(sIdx, { description: e.target.value })
                  }
                />
              </FormField>
            </div>
            <label className="flex items-center gap-space-2 mt-space-2 text-caption">
              <input
                type="checkbox"
                checked={section.isRequired}
                onChange={(e) =>
                  updateSection(sIdx, { isRequired: e.target.checked })
                }
                className="h-4 w-4 accent-gold"
              />
              Section is required
            </label>

            <div className="mt-space-3 space-y-space-2">
              {section.questions.map((q, qIdx) => (
                <QuestionRow
                  key={q.id ?? `q-${qIdx}`}
                  q={q}
                  qIdx={qIdx}
                  total={section.questions.length}
                  onChange={(patch) => updateQuestion(sIdx, qIdx, patch)}
                  onMove={(dir) => moveQuestion(sIdx, qIdx, dir)}
                  onRemove={() => removeQuestion(sIdx, qIdx)}
                />
              ))}
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => addQuestion(sIdx)}
              >
                <Plus size={14} aria-hidden /> Question
              </Button>
            </div>
          </div>
        ))}

        <Button type="button" variant="secondary" onClick={addSection}>
          <Plus size={14} aria-hidden /> Section
        </Button>
      </div>
    </section>
  );
}

function QuestionRow({
  q,
  qIdx,
  total,
  onChange,
  onMove,
  onRemove,
}: {
  q: UpsertReportFormQuestionPayload;
  qIdx: number;
  total: number;
  onChange: (patch: Partial<UpsertReportFormQuestionPayload>) => void;
  onMove: (dir: -1 | 1) => void;
  onRemove: () => void;
}) {
  const isPicker =
    q.questionType === "LoanPicker" ||
    q.questionType === "PortfolioPicker" ||
    q.questionType === "BrokerPicker" ||
    q.questionType === "VendorPicker" ||
    q.questionType === "DealPicker";
  return (
    <div className="bg-white-pure rounded-md border border-border-light p-space-2">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-space-2">
        <FormField label={`Question ${qIdx + 1}`} required>
          <Input
            value={q.questionText}
            onChange={(e) => onChange({ questionText: e.target.value })}
          />
        </FormField>
        <FormField label="Type">
          <Select
            value={q.questionType}
            onChange={(e) =>
              onChange({ questionType: e.target.value as QuestionType })
            }
          >
            {QUESTION_TYPES.map((t) => (
              <option key={t} value={t}>
                {questionTypeLabel(t)}
              </option>
            ))}
          </Select>
        </FormField>
        <FormField label="Help text (optional)">
          <Input
            value={q.helpText ?? ""}
            onChange={(e) => onChange({ helpText: e.target.value })}
          />
        </FormField>
      </div>
      {(q.questionType === "Select" || q.questionType === "MultiSelect") && (
        <FormField
          label="Options (JSON array)"
          className="mt-space-2"
          helperText='Example: ["Option A", "Option B", "Option C"]'
        >
          <Input
            placeholder='["Option A", "Option B"]'
            value={q.optionsJson ?? ""}
            onChange={(e) => onChange({ optionsJson: e.target.value })}
          />
        </FormField>
      )}
      <ValidationRulesEditor
        questionType={q.questionType}
        value={q.validationRulesJson ?? ""}
        onChange={(next) => onChange({ validationRulesJson: next })}
      />
      <div className="flex items-center justify-between mt-space-2">
        <div className="flex items-center gap-space-3">
          <label className="flex items-center gap-space-2 text-caption">
            <input
              type="checkbox"
              checked={q.isRequired}
              onChange={(e) => onChange({ isRequired: e.target.checked })}
              className="h-4 w-4 accent-gold"
            />
            Required
          </label>
          {isPicker && (
            <Badge status="gold">
              Auto-resolves placeholders
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-space-1">
          <Button
            type="button"
            variant="tertiary"
            size="sm"
            onClick={() => onMove(-1)}
            disabled={qIdx === 0}
            aria-label="Move question up"
          >
            <ChevronUp size={14} aria-hidden />
          </Button>
          <Button
            type="button"
            variant="tertiary"
            size="sm"
            onClick={() => onMove(1)}
            disabled={qIdx === total - 1}
            aria-label="Move question down"
          >
            <ChevronDown size={14} aria-hidden />
          </Button>
          <Button
            type="button"
            variant="tertiary"
            size="sm"
            onClick={onRemove}
            aria-label="Remove question"
          >
            <Trash2 size={14} aria-hidden />
          </Button>
        </div>
      </div>
    </div>
  );
}

export function newSection(index: number): UpsertReportFormSectionPayload {
  return {
    title: `Section ${index + 1}`,
    description: "",
    displayOrder: index,
    isRequired: false,
    questions: [newQuestion(0)],
  };
}

export function newQuestion(index: number): UpsertReportFormQuestionPayload {
  return {
    questionText: "",
    helpText: "",
    questionType: "Text",
    optionsJson: "",
    validationRulesJson: "",
    isRequired: false,
    displayOrder: index,
  };
}

/**
 * v3.5 §7 — single-row ghost preview of a locked system field. Visual
 * reflection of what the user-facing form renders for the "Report
 * context" section. Inputs are aria-disabled + tabIndex=-1 so screen
 * readers + keyboard navigation skip them.
 */
function GhostPreviewField({
  label,
  optional,
  helper,
}: {
  label: string;
  optional?: boolean;
  helper: string;
}) {
  return (
    <div>
      <div className="font-display text-caption text-onyx mb-[2px]">
        {label}
        {optional && (
          <span className="ml-space-2 text-caption italic text-gray-mid">
            (Optional)
          </span>
        )}
      </div>
      <p className="text-caption text-gray-mid mb-[4px]">{helper}</p>
      <div
        aria-disabled="true"
        tabIndex={-1}
        className="h-9 rounded-md border border-border-light bg-white-warm/50 px-space-3 flex items-center text-caption italic text-gray-mid select-none"
      >
        Short answer field — preview, disabled
      </div>
    </div>
  );
}
